import _ from 'lodash';

type MultiMap<T> = DictionaryStrict<T[]>;

export function removeValueFromMultimap<T>(multiMap: MultiMap<T>, valueToRemove: T): MultiMap<T> {
  const withValueRemoved = _.mapValues(
    multiMap,
    (v) => v && v.filter((item) => item !== valueToRemove)
  );
  return _.omitBy(withValueRemoved, _.isEmpty);
}

export function removeKeyValueFromMultimap<T>(
  multiMap: MultiMap<T>,
  key: number,
  valueToRemove: T
): MultiMap<T> {
  const prevValues = multiMap[key] || [];
  return _.omitBy({ ...multiMap, [key]: prevValues.filter((v) => v !== valueToRemove) }, _.isEmpty);
}

export function addValueToMultimap<T>(multiMap: MultiMap<T>, key: number, value: T) {
  const oldSet = multiMap[key] || [];
  return { ...multiMap, [key]: oldSet.includes(value) ? oldSet : _.sortBy([value, ...oldSet]) };
}

export function insertAllToMultimap<T>(
  multiMap: MultiMap<T>,
  key: number,
  values: T[]
): MultiMap<T> {
  const oldSet = multiMap[key] || [];
  return { ...multiMap, [key]: _.sortedUniq(_.sortBy([...values, ...oldSet])) };
}

// saving for posterity, re-export it if it is ever needed/useful
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function moveValueInMultimap<T>(multiMap: MultiMap<T>, key: number | null | undefined, value: T) {
  return key
    ? { ...addValueToMultimap(removeValueFromMultimap(multiMap, value), key, value) }
    : removeValueFromMultimap(multiMap, value);
}

export function getKeysFromValueInMultimap<T>(multiMap: MultiMap<T>, value: T): string[] {
  return _.keys(_.pickBy(multiMap, (values) => values && values.includes(value)));
}

export function getValuesFromKeyInMultimap<T>(
  multiMap: MultiMap<T>,
  key: number | null | undefined
): T[] {
  return key ? multiMap[key] || [] : [];
}

export function mapStrictValuesToList<T, S>(
  d: DictionaryStrict<T>,
  f: (x: T, key?: string) => S
): S[];
export function mapStrictValuesToList<T, S>(
  d: DictionaryStrict<T>,
  f: (x: T, key?: string) => S
): S[];
export function mapStrictValuesToList<T, S>(d: object, f: (t: T, key?: string) => S): S[] {
  const returnValue: S[] = [];
  for (const [k, v] of _.toPairs(d)) {
    if (v) {
      returnValue.push(f(v, k));
    }
  }
  return returnValue;
}

export function mapStrictValues<T, S>(
  d: DictionaryStrict<T>,
  f: (x: T, key?: string) => S
): DictionaryStrict<S>;
export function mapStrictValues<T, S>(
  d: DictionaryStrict<T>,
  f: (x: T, key?: string) => S
): DictionaryStrict<S>;
export function mapStrictValues<T, S>(d: object, f: (t: T, key?: string) => S): object {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const returnValue: any = {};
  for (const [k, v] of _.toPairs(d)) {
    if (v) {
      returnValue[k] = f(v, k);
    }
  }
  return returnValue;
}
